import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import styles from './ShareSheet.module.css';

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
  photoUrl: string;
  albumName: string;
  eventName: string;
}

// S3 presigned URLs cannot be fetched cross-origin from pixelmachha.com.
// Convert them to CloudFront CDN URLs, which have proper CORS headers.
function toCdnUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('pixel-machha-photos.s3')) {
      return `https://d3ic3ju44zc444.cloudfront.net${parsed.pathname}`;
    }
  } catch {
    // not a valid URL — return as-is
  }
  return url;
}

async function fetchPhotoAsFile(photoUrl: string, filename: string): Promise<File> {
  const res = await fetch(toCdnUrl(photoUrl));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return new File([blob], filename, { type: 'image/jpeg' });
}

export function ShareSheet({ isOpen, onClose, photoUrl, albumName, eventName }: ShareSheetProps) {
  const [sharing, setSharing] = useState(false);

  const shareText = eventName
    ? `😃 Hey ! Check out this photo from album "${albumName}" from the event "${eventName}" from PixelMachha.com`
    : `😃 Hey ! Check out this photo from album "${albumName}" from PixelMachha.com`;

  const filename = `${albumName.replace(/\s+/g, '-').toLowerCase()}-photo.jpg`;

  // ── Native share (mobile) ───────────────────────────────────────────────
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.canShare;

  const handleNativeShare = useCallback(async () => {
    setSharing(true);
    try {
      const file = await fetchPhotoAsFile(photoUrl, filename);
      const shareData: ShareData = {
        title: albumName,
        text: shareText,
        files: [file],
      };

      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: share without file
        await navigator.share({ title: albumName, text: shareText, url: photoUrl });
      }
    } catch (err) {
      // User cancelled share — not an error
      if ((err as Error)?.name !== 'AbortError') {
        toast.error('Share failed. Try another method.');
      }
    } finally {
      setSharing(false);
    }
  }, [photoUrl, filename, albumName, shareText]);

  // ── Download ────────────────────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    try {
      const res = await fetch(photoUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  }, [photoUrl, filename]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const shareActions = [
    ...(canNativeShare
      ? [{
        key: 'native',
        label: sharing ? 'Sharing...' : 'Share...',
        icon: <Share2 className={styles['icon-lg']} />,
        onClick: handleNativeShare,
        accent: true,
      }]
      : []),
    {
      key: 'download',
      label: 'Download',
      icon: <Download className={styles['icon-lg']} />,
      onClick: handleDownload,
      accent: false,
    },
  ];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles['backdrop']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={handleClose}
          />

          {/* Bottom sheet */}
          <motion.div
            className={styles['sheet-container']}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            <div className={styles['sheet']}>
              {/* Handle bar */}
              <div className={styles['handle-row']}>
                <div className={styles['handle']} />
              </div>

              {/* Header */}
              <div className={styles['header']}>
                <h3 className={styles['title']}>
                  Share Photo
                </h3>
                <button
                  onClick={handleClose}
                  className={styles['close-btn']}
                  aria-label="Close"
                >
                  <X className={styles['icon-lg']} />
                </button>
              </div>

              {/* Photo preview */}
              <div className={styles['preview-section']}>
                <div className={styles['preview-frame']}>
                  <img
                    src={photoUrl}
                    alt="Photo to share"
                    className={styles['preview-image']}
                  />
                </div>
              </div>

              {/* Share actions grid */}
              <div className={styles['actions-grid']}>
                {shareActions.map((action) => (
                  <motion.button
                    key={action.key}
                    onClick={action.onClick}
                    disabled={action.key === 'native' && sharing}
                    className={[
                      styles['action-btn'],
                      action.accent ? styles['action-btn-accent'] : styles['action-btn-default'],
                    ].join(' ')}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    <span className={
                      action.accent
                        ? styles['action-icon-accent']
                        : styles['action-icon-default']
                    }>
                      {action.icon}
                    </span>
                    <span className={
                      action.accent
                        ? styles['action-label-accent']
                        : styles['action-label-default']
                    }>
                      {action.label}
                    </span>
                  </motion.button>
                ))}
              </div>

              {/* Safe area padding */}
              <div className={styles['safe-area']} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
